import React, {FC, useCallback, useContext, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';

import {
  trackAddPanelMenuClosed,
  trackImportPanelMenuClosed,
} from '../../../services/analytics';
import * as ReportViewsSelectors from '../../../state/reports/selectors';
import * as ReportsTypes from '../../../state/reports/types';
import * as ViewHooks from '../../../state/views/hooks';
import * as PanelViewTypes from '../../../state/views/panel/types';
import * as PanelBankSectionConfigActions from '../../../state/views/panelBankSectionConfig/actions';
import * as SectionViewTypes from '../../../state/views/section/types';
import AddVisMenu from '../../AddVisMenu';
import {AddVisNewPanelEditor} from '../../AddVisNewPanelEditor';
import {OrganizedSettings} from '../../PanelBank/types';
import {WBSlateReduxBridgeContext} from '../../Slate/WBSlateReduxBridgeContext';
import ImportPanelMenu from '../ImportPanelMenu';
import {REPORT_PANEL_GRID_MENU} from '../types';
import {ReportPanelGridButton} from './ReportPanelGridButton';
import * as S from './ReportPanelGridSection.styles';

type ReportPanelGridActionsProps = {
  entityName: string;
  projectName: string;
  sectionPart: SectionViewTypes.SectionNormalized;
  viewRef: ReportsTypes.ReportViewRef;
  openedMenu?: REPORT_PANEL_GRID_MENU;
  setOpenedMenu: (menu?: REPORT_PANEL_GRID_MENU) => void;
};

export const ReportPanelGridActions: FC<ReportPanelGridActionsProps> = ({
  sectionPart,
  entityName,
  projectName,
  viewRef,
  openedMenu,
  setOpenedMenu,
}) => {
  const [newPanel, setNewPanel] = useState<PanelViewTypes.Panel>();
  const [isEditable, setIsEditable] = useState(false);

  const addPanelByRef = ViewHooks.useViewAction(
    sectionPart.panelBankSectionConfigRef,
    PanelBankSectionConfigActions.addPanelByRef
  );

  const reportWidth = useSelector(ReportViewsSelectors.getReportWidth(viewRef));

  // Using callback prevents recomp on deselect
  const onSelectCallback = useCallback(
    (panel, skipEdit) => {
      setOpenedMenu(undefined);
      setNewPanel(panel);
      setIsEditable(!!skipEdit);
    },
    [setOpenedMenu]
  );

  const {panelSettingsRef} = useContext(WBSlateReduxBridgeContext);
  const panelSettings = ViewHooks.usePartMaybe(panelSettingsRef);
  const inheritedSettings: OrganizedSettings = useMemo(() => {
    return {
      linePlot: panelSettings,
    };
  }, [panelSettings]);

  return (
    <>
      <S.PanelButtonWrapper>
        <ReportPanelGridButton
          buttonText="Import panel"
          openedMenu={openedMenu}
          menu={REPORT_PANEL_GRID_MENU.IMPORT_PANEL}
          setOpenedMenu={setOpenedMenu}
          analytics={trackImportPanelMenuClosed}
          menuPopup={
            <ImportPanelMenu
              entityName={entityName}
              projectName={projectName}
              sectionPart={sectionPart}
            />
          }
        />
        <ReportPanelGridButton
          buttonText="Add panel"
          dataTest="add vis"
          openedMenu={openedMenu}
          menu={REPORT_PANEL_GRID_MENU.ADD_PANEL}
          setOpenedMenu={setOpenedMenu}
          analytics={trackAddPanelMenuClosed}
          menuPopup={
            <AddVisMenu entityName={entityName} onSelect={onSelectCallback} />
          }
        />
      </S.PanelButtonWrapper>
      {newPanel && (
        <AddVisNewPanelEditor
          panelBankSectionConfigRef={sectionPart.panelBankSectionConfigRef}
          currentHeight={400}
          inheritedSettings={inheritedSettings}
          initialEditingState={true}
          panel={newPanel}
          noEdit={isEditable}
          onCancel={() => {
            setNewPanel(undefined);
          }}
          onOK={panelRef => {
            setNewPanel(undefined);
            addPanelByRef(panelRef, false, reportWidth === 'readable');
          }}
        />
      )}
    </>
  );
};
